import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, TOKEN_KEY, REFRESH_TOKEN_KEY } from '../utils/constants';
import { clearAuthStorage } from '../utils/authStorage';

class ApiService {
  private api: AxiosInstance;
  // Single-flight token refresh: concurrent 401s (e.g. the burst of chart polls
  // fired when the tab regains focus after the JWT expired) must share ONE
  // refresh call. Otherwise the first refresh rotates the (single-use) refresh
  // token and the others fail with the now-stale token → spurious logout.
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      // Round 33 — bumped from 10s to 60s. Endpoints like /trades/sync,
      // /positions/refresh and several broker round-trips routinely take
      // longer than 10s on the live VPS, producing "POST /xxx timed out"
      // toasts even when the call eventually succeeds on the server.
      // 60s is comfortably above the slowest observed broker pull (~25s
      // for Delta during peak) while still failing fast on actual hangs.
      timeout: 60000,
      // H3 / F3 — send httpOnly auth cookies on every request. Required
      // for both the new cookie-based session AND the eg_csrf cookie that
      // the CSRF interceptor (below) reads + echoes back.
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // H3 / F3 — for the cutover window, still send the legacy
        // Authorization: Bearer header IF localStorage has a token. The
        // backend prefers the cookie when both are present, so on the
        // very next refresh-or-login the legacy token gets superseded
        // and a future deploy can drop this fallback entirely.
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) config.headers.Authorization = `Bearer ${token}`;

        // H9 / F3 — CSRF double-submit. Read eg_csrf from document.cookie
        // and echo it in X-CSRF-Token on every mutating request. The
        // backend csrf middleware skips safe methods + Bearer-auth + no-
        // cookie requests so this is harmless on read calls.
        const method = (config.method || 'get').toLowerCase();
        if (method !== 'get' && method !== 'head' && method !== 'options') {
          const csrf = document.cookie.split('; ').find(c => c.startsWith('eg_csrf='));
          if (csrf) {
            (config.headers as any)['X-CSRF-Token'] = decodeURIComponent(csrf.slice('eg_csrf='.length));
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Round 63 — automatic retry on gateway-level errors (502/503/504).
        // These are almost always transient: nginx can't reach the backend
        // for a few seconds during a container restart, or the upstream is
        // momentarily unhealthy. Retrying once with a small delay swallows
        // the brief deploy-window 502s so the user doesn't see a popup.
        const cfg = error.config || {};
        const status = error.response?.status;
        const isGatewayErr = status === 502 || status === 503 || status === 504;
        const errCode = error.response?.data?.error?.code || '';
        const isBrokerIpWhitelist = typeof errCode === 'string' && errCode === 'IP_NOT_WHITELISTED';
        if (isGatewayErr && !isBrokerIpWhitelist && !(cfg as any).__retried) {
          (cfg as any).__retried = true;
          // 1.5s pause — long enough for a quick container restart to finish,
          // short enough that the user barely notices.
          await new Promise(r => setTimeout(r, 1500));
          try {
            return await this.api(cfg);
          } catch {
            // Fall through to the normal error handling below
          }
        }

        // Handle 401 authentication errors — but ONLY for JWT session failures.
        // Broker credential failures (Delta Exchange API key invalid) come back as 503
        // from our backend, so a raw 401 here means our own JWT is expired/invalid.
        if (error.response?.status === 401) {
          // Ignore 401s that are broker-auth related (should now be 503, but guard anyway)
          const errCode = error.response?.data?.error?.code || error.response?.data?.error || '';
          const isBrokerAuth = typeof errCode === 'string' && (
            errCode === 'BROKER_AUTH_ERROR' || errCode === 'IP_NOT_WHITELISTED'
          );
          // Don't attempt to refresh more than once per request (avoids a loop
          // if the freshly-refreshed token is also rejected).
          if (!isBrokerAuth && !(cfg as any).__refreshRetried) {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (refreshToken) {
              try {
                // SINGLE-FLIGHT: if a refresh is already in progress, await it
                // instead of starting another (which would consume an already-
                // rotated refresh token and fail). All concurrent 401s share the
                // one refresh and then retry with the new token.
                if (!this.refreshPromise) {
                  this.refreshPromise = this.refreshToken(refreshToken)
                    .then((response) => {
                      localStorage.setItem(TOKEN_KEY, response.data.token);
                      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
                      return response.data.token as string;
                    })
                    .finally(() => { this.refreshPromise = null; });
                }
                const newToken = await this.refreshPromise;
                // Retry original request with the refreshed token.
                (cfg as any).__refreshRetried = true;
                cfg.headers = cfg.headers || {};
                (cfg.headers as any).Authorization = `Bearer ${newToken}`;
                return this.api(cfg);
              } catch (refreshError: any) {
                // Only log out when the refresh was GENUINELY rejected (the
                // refresh token itself is invalid/expired → backend returns
                // 401/403). A transient failure (Network Error / timeout / 5xx
                // because the backend was briefly overloaded/unreachable) must
                // NOT clear the session — the access token is likely still
                // refreshable; the next request retries once the backend
                // recovers. This prevents spurious logouts during load spikes.
                const rStatus = refreshError?.response?.status;
                const refreshRejected = rStatus === 401 || rStatus === 403;
                if (refreshRejected) {
                  // Session keys ONLY — chart/indicator/★ preferences must survive the
                  // forced re-login (localStorage.clear() here was why every VPS
                  // restart reset the chart page to defaults).
                  clearAuthStorage();
                  window.location.href = '/login';
                }
                return Promise.reject(error); // don't fall through to the toast
              }
            } else {
              // No refresh token, redirect to login
              window.location.href = '/login';
              return Promise.reject(error);
            }
          }
        }

        // Don't show toast for IP whitelist errors (503 with IP_NOT_WHITELISTED)
        // These are handled specifically in DashboardLayout
        const isIpWhitelistError = error.response?.status === 503 &&
          error.response?.data?.error?.code === 'IP_NOT_WHITELISTED';

        // Allow callers to suppress the global toast by passing { headers: { _suppressToast: 'true' } }
        const isSuppressed = error.config?.headers?.['_suppressToast'] === 'true';

        // Suppress GET 404s globally — they're often benign (empty resource / not-yet-created).
        // Callers who truly care can check the rejected promise and surface a local message.
        const method = (error.config?.method || 'get').toLowerCase();
        const isGet404 = error.response?.status === 404 && method === 'get';

        // Suppress 502 / 504 toasts globally — these are nginx-level (no
        // upstream reachable / upstream timed out) and almost always
        // transient deploy-window blips. We already retried once above;
        // a second failure still isn't user-actionable. 503 is left alone
        // because the backend uses it for meaningful errors like broker-
        // auth (IP_NOT_WHITELISTED has its own suppression above).
        const isGatewaySoftFail = status === 502 || status === 504;

        // Never toast a 401 — it's handled above (silent refresh, or redirect to
        // login). A leftover "GET /user/settings → 401" popup is pure noise.
        const isAuth401 = status === 401;

        // Suppress timeout toasts for GET (reads / background polls) — these are
        // transient backend-slowness blips, not user-actionable. Mutations
        // (POST/PUT/DELETE) still surface a timeout so the user knows an action
        // may not have completed.
        const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
        const isGetTimeout = method === 'get' && isTimeout;

        // Suppress "Network Error" toasts for GET (reads / background polls) —
        // these mean the request never got a response (backend briefly
        // unreachable / event-loop saturated), which is a transient blip the
        // next poll recovers from. Not user-actionable, and spamming a toast per
        // failed background poll is pure noise. Mutations still surface it.
        const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || ''));
        const isGetNetworkError = method === 'get' && isNetworkError;

        // Suppress 429 toasts for GET reads (chart/option fan-out, background polls).
        // A rate-limit on a read is transient and self-heals on the next poll / once
        // the user settles — popping "Too many requests" per request while browsing is
        // pure noise. Candle reads surface a subtle inline retry hint instead.
        // Mutations (POST/PUT/DELETE) still toast a 429 so the user knows an action
        // was throttled.
        const isGet429 = status === 429 && method === 'get';

        if (!isIpWhitelistError && !isSuppressed && !isGet404 && !isGatewaySoftFail && !isAuth401 && !isGetTimeout && !isGetNetworkError && !isGet429) {
          // Build a maximally informative fallback when the backend didn't
          // supply a structured error.message. The previous "An error
          // occurred" string told the user nothing — when they reported it
          // we had to guess which call failed. Now surface the HTTP status
          // and the request URL/method so the user can copy-paste the
          // toast text into a bug report and the source request is obvious.
          const backendMsg = error.response?.data?.error?.message
            || error.response?.data?.message
            || (typeof error.response?.data === 'string' ? error.response.data : '');
          let message: string = backendMsg as string;
          if (!message) {
            const status = error.response?.status;
            const url = error.config?.url || '(unknown)';
            const m = (error.config?.method || 'GET').toUpperCase();
            if (status) {
              message = `${m} ${url} → ${status}`;
            } else if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
              message = `${m} ${url} timed out`;
            } else if (error.message) {
              message = `${m} ${url}: ${error.message}`;
            } else {
              message = `${m} ${url} failed`;
            }
          }
          // Log the full error to console so a paste of the dev-tools log
          // captures status + body + headers when the user reports a bug.
          // eslint-disable-next-line no-console
          console.error('[api]', error.config?.method, error.config?.url,
            error.response?.status, error.response?.data, error);
          toast.error(message, { id: message });
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(refreshToken: string) {
    // H3 / F3 — cookies travel automatically because withCredentials=true.
    // The body argument is kept for the cutover window so older backends
    // still in flight can read it; once the cookie path is the only
    // option, drop the parameter altogether.
    return axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { withCredentials: true });
  }

  // Generic methods. T defaults to `any` so untyped callers get a usable
  // `response.data` (without it, T resolved to `{}` and forced `as any` casts).
  async get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.api.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.api.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.api.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.api.delete<T>(url, config);
  }
}

export default new ApiService();
